import ErrorMessage from "components/common/ErrorMessage";
import LiveClock from "components/common/LiveClock";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { fetchScheduleSummaryList } from "slices/scheduleSlice";
import { Select } from "components/common/Select";
import ListTable from "components/dashboard/ListTable";
import { fetchDashboardAnalytics } from "slices/dashboardSlice";
import { fetchDashboardAppointments } from "slices/dashboardSlice";
import { DashboardSelector } from "slices/dashboardSlice";
import { connectQuickBooks } from "slices/dashboardSlice";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { fetchDepartments } from "slices/departmentsSlice";
import { departmentsSelector } from "slices/departmentsSlice";
import { fetchFacilities } from "slices/facilitiesSlice";
import { facilitiesSelector } from "slices/facilitiesSlice";
import AddAppointment from "components/appointment/AddAppointment";
import { fetchAppointments } from "slices/appointmentSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from "react-select-async-paginate";
import { selectCustomStyles } from "components/common/Select";
import FullPageLoader from "components/common/FullPageLoader";
import { usePermissions } from "layouts/Admin";
import { LegendButton } from "components/common/TableColor";
import { legends } from "components/common/TableColor";
import { userSelector } from "slices/userSlice";
import { getCheckInStatus } from "slices/userSlice";
import { Link } from "react-router-dom";

const DatesInputs = ({ control, errors }) => {
  const startDateRef = React.useRef(null);
  const endDateRef = React.useRef(null);

  return (
    <>
      <div className="row-primary">
        <h2>Start Date*</h2>
        <Controller
          name="start_date"
          control={control}
          render={({ field }) => (
            <div className="input-image basic_bases">
              <input
                ref={startDateRef}
                id="start_date"
                type="date"
                name="start_date"
                placeholder="Start Date"
                {...field}
                value={
                  field.value ? moment(field.value).format("YYYY-MM-DD") : ""
                }
                onChange={(e) => {
                  const dateString = moment(new Date(e.target.value)).format(
                    "YYYY/MM/DD"
                  );
                  field.onChange(dateString);
                }}
              />
              <img
                src="/images/celender.png"
                alt="celender Icon"
                className="custom-icon"
              />
            </div>
          )}
        />
      </div>
      <div className="row-primary">
        <h2>End Date*</h2>
        <Controller
          name="end_date"
          control={control}
          render={({ field }) => (
            <div className="input-image basic_bases">
              <input
                ref={endDateRef}
                type="date"
                name="end_date"
                placeholder="End Date"
                {...field}
                value={
                  field.value ? moment(field.value).format("YYYY-MM-DD") : ""
                }
                onChange={(e) => {
                  const dateString = moment(new Date(e.target.value)).format(
                    "YYYY/MM/DD"
                  );
                  field.onChange(dateString);
                }}
              />
              <img
                src="/images/celender.png"
                alt="celender Icon"
                className="custom-icon"
              />
            </div>
          )}
        />
      </div>
    </>
  );
};

function Dashboard() {
  const { checkPermission } = usePermissions();
  const dispatch = useDispatch();
  const {
    dashboard,
    last_page: totalPages,
    loader,
    total,
  } = useSelector(DashboardSelector);
  const { language: languages } = useSelector(languageSelector);
  const { departments } = useSelector(departmentsSelector);
  const { facilities } = useSelector(facilitiesSelector);
  const [pageLength, setPageLength] = useState(10);
  const {
    clearErrors,
    handleSubmit,
    watch,
    formState: { errors },
    control,
    setValue,
    setError: setErrors,
    reset,
  } = useForm({
    defaultValues: {
      start_date: moment().startOf("month").format("YYYY-MM-DD"),
      end_date: moment().endOf("month").format("YYYY-MM-DD"),
    },
  });

  const [statusValue, setStatusValue] = React.useState("all");
  const [filters, setFilters] = useState({
    status: statusValue,
    start_date: moment().startOf("month").format("YYYY-MM-DD"),
    end_date: moment().endOf("month").format("YYYY-MM-DD"),
    service: "",
    language_id: "",
  });

  const [activeLegend, setActiveLegend] = useState(0);

  useEffect(() => {
    dispatch(fetchDashboardAnalytics());
    dispatch(
      fetchDashboardAppointments(1, {
        pageLength: 10,
        start_date: moment().startOf("month").format("YYYY-MM-DD"),
        end_date: moment().endOf("month").format("YYYY-MM-DD"),
        sortBy: "id",
        sortDirection: "desc",
      })
    );
  }, []);

  // #region Language dropdown
  const filteredLanguages = React.useMemo(
    () =>
      languages?.map((language) => {
        const object = {
          value: language?.id,
          label: language?.name,
        };
        return object;
      }),
    [languages]
  );

  const filteredLanguagesLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchLanguages(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion

  //#region Department dropdown
  const filtereddepartment = React.useMemo(
    () =>
      departments?.map((department) => {
        const object = {
          value: department?.id,
          label: department?.name,
        };
        return object;
      }),
    [departments]
  );
  const filtereddepartmentLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchDepartments(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion

  //#region Facility dropdown
  const filteredFacilities = React.useMemo(
    () =>
      facilities?.map((facility) => {
        const object = {
          value: facility?.id,
          label: facility?.abbreviation,
        };
        return object;
      }),
    [facilities]
  );

  const filteredFacilitiesLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchFacilities(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.abbreviation,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion

  // #region Legends
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...legends];
    temp_array.unshift({
      value: "all",
      name: "All",
      id: 0,
      color: "#3176A0",
    });
    return temp_array;
  }, [legends]);

  const hanldeActiveLegend = (id, value) => {
    setActiveLegend(id);
    setStatusValue(value);

    const newFilters = {
      ...filters,
      pagelength: pageLength,
      status: value,
    };
    setFilters(newFilters);

    dispatch(fetchDashboardAppointments(1, { ...newFilters, sortBy: 'id', sortDirection: 'desc' }));
  };

  //#endregion

  const onSubmit = (data) => {
    const newFilters = {
      ...filters,
      ...data,
      pagelength: pageLength,
    };
    setFilters(newFilters);
    dispatch(fetchDashboardAppointments(1, { ...newFilters }));
  };

  const handleResetFields = () => {
    setValue("start_date", null);
    setValue("end_date", null);
    setValue("language_id", "");
    setValue("interpreter", "");
    setValue("department_id", "");
    setValue("facility_id", "");
    setValue("service", "");
    dispatch(
      fetchDashboardAppointments(1, { sortBy: "id", sortDirection: "desc" })
    );
    clearErrors();
  };

  const serviceOption = [
    { value: "In Person", label: "In Person" },
    { value: "OTP Pre Schedule", label: "OTP Pre Schedule" },
    {
      value: "Video Remote Interpretation",
      label: "Video Remote Interpretation",
    },
    { value: "OPI On Demand", label: "OPI On Demand" },
  ];
  const [showWebPhone, setShowWebPhone] = useState(false);

  const openWebPhone = () => {
    setShowWebPhone(!showWebPhone);
    // window?.WebPhoneSDK && new window.WebPhoneSDK({
    //   parentElement: '#webphone',
    //   just_url: "telephony.algovisolutions.com",
    //   wss_url: 'wss://telephony.algovisolutions.com:8089/asterisk/ws',
    //   extension: '2000',
    //   extension_password: '2IpN1MFZgeWOaQzYU1At',
    //   from_cid: '9218764328',
    //   mediaElement: 'media_player',
    //   callCardElement: 'callCards',
    //   attempt_auto_register: true
    // })
  };

  const [isFourteenDays, setIsFourteenDays] = useState(false);
  const tableRef = useRef(null);

  const handleFilter = (item, isFourteen) => {
    const today = moment().format("YYYY-MM-DD");
    const nextFourteenDays = moment().add(14, "days").format("YYYY-MM-DD");

    const newFilters = {
      ...filters,
      start_date: today,
      end_date: isFourteen ? nextFourteenDays : today,
      service: item?.type || "",
    };

    setFilters(newFilters);
    dispatch(fetchDashboardAppointments(1, { ...newFilters }));
  };

  const [checkinStatus, setCheckinStatus] = useState();
  const [userRole, setUserRole] = useState("");

  useEffect(() => {

    dispatch(getCheckInStatus((success, data) => {
      if (success) {
        setCheckinStatus(data.data.user.checked_in);
        setUserRole(data.data.user.role);
      } else {
        console.log("Failed to fetch check-in status", data);
      }
    }));
  }, [checkinStatus]);

  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Dashboard</h2>
          <div>
            {checkPermission("Webphone Access") && checkinStatus === 1 && (
              <button
                className="Cancle_button me-2"
                type="button"
                data-bs-toggle="offcanvas"
                data-bs-target="#offcanvasExample"
                aria-controls="offcanvasExample"
                onClick={openWebPhone}
              >
                Web Phone
              </button>
            )}
            {userRole === "admin" && (
              <Link to={`${process.env.REACT_APP_BACKEND_URL_LOCAL}/api/quickbooks/connect`} target="_blank" className="main_button  green me-2" type="button">Connect with QuickBooks</Link>
            )}
          </div>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Today</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr
                      className="cursorPointer"
                      key={index}
                      onClick={() => {
                        setIsFourteenDays(false);
                        handleFilter(service, false);
                      }}
                    >
                      <td>{service?.type}</td>
                      <td>{service?.count}</td>
                      <td>{service?.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Next 14 Days</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map(
                    (service, index) => (
                      <tr
                        className="cursorPointer"
                        key={index}
                        onClick={() => {
                          setIsFourteenDays(true);
                          handleFilter(service, true);
                        }}
                      >
                        <td>{service?.type}</td>
                        <td>{service?.count}</td>
                        <td>{service?.count}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* <ScheduleModal /> */}

        <div className="card_table">
          <div className="button-sm forLegends p-3">
            <LegendButton
              activeId={activeLegend}
              options={filteredStatuses}
              handleClick={hanldeActiveLegend}
            />
          </div>
          <form className="search_form_ad">
            <div className="row-primary dahboard_filter">
              <DatesInputs control={control} errors={errors} />
              <div className="row-primary">
                <h2>Interpreter</h2>
                <Controller
                  name="interpreter"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input
                        className="input-in type_inp"
                        name="interpreter"
                        type="text"
                        placeholder="Interpreter name"
                        {...field}
                      />
                      {errors?.register_id?.message && (
                        <div style={{ marginBottom: 10 }}>
                          <ErrorMessage text={errors?.register_id?.message} />
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
              {/* <div className="row-primary">
                <h2>Facility</h2>
                <Controller
                  name="facility_id"
                  control={control}
                  render={({ field }) => (
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement="auto"
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={
                          field.value
                            ? filteredFacilities?.find(
                                (opt) => field.value == opt.value
                              )
                            : ""
                        }
                        loadOptions={filteredFacilitiesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  )}
                />
              </div> */}
              <div className="row-primary">
                <h2>Language</h2>
                <Controller
                  name="language_id"
                  control={control}
                  render={({ field }) => (
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement="auto"
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={
                          field.value
                            ? filteredLanguages?.find(
                              (opt) => field.value == opt.value
                            )
                            : ""
                        }
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  )}
                />
              </div>
              <div className="row-primary">
                <h2>Services</h2>
                <Controller
                  name="service"
                  control={control}
                  render={({ field }) => (
                    <div className="basic_bases">
                      <Select
                        name="service"
                        options={serviceOption || []}
                        {...field}
                        value={
                          field.value
                            ? serviceOption?.find(
                              (opt) => field.value == opt.value
                            )
                            : ""
                        }
                      />
                    </div>
                  )}
                />
              </div>
              {/* <div className="row-primary">
                <h2>Department</h2>
                <Controller
                  name="department_id"
                  control={control}
                  render={({ field }) => (
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement="auto"
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={
                          field.value
                            ? filtereddepartment?.find(
                                (opt) => field.value == opt.value
                              )
                            : ""
                        }
                        loadOptions={filtereddepartmentLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  )}
                />
              </div> */}
            </div>

            <div className="row-primary btns_area row_10">
              <SubmitButton
                handleSubmit={handleSubmit(onSubmit)}
                buttonType="button"
                btnText="Search"
                className={"green"}
              />
              <SubmitButton
                buttonType="button"
                className={"lightBlue"}
                btnText="Reset"
                handleSubmit={handleResetFields}
              />
            </div>

            <div ref={tableRef}>
              <ListTable
                data={dashboard?.appointments}
                filters={filters}
                setFilters={setFilters}
                setPageLength={setPageLength}
              />
            </div>
          </form>
        </div>
      </div>

      {loader && <FullPageLoader />}
    </>
  );
}

export default Dashboard;
